import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Reveal, MaskedLine, SectionLabel } from "@/components/Reveal";

const HERO_IMG = "/assets/aurelie.png";
const EASE = [0.22, 1, 0.36, 1];
const IMG_ANIM = { initial: { opacity: 0, scale: 0.96 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 1, delay: 0.5, ease: EASE } };
const BADGE_ANIM = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay: 1.1 } };
const CLIP = { clipPath: "inset(0 round 1rem)" };

const HeroText = () => (
  <div className="lg:col-span-7">
    <Reveal delay={0.1}>
      <SectionLabel>Gestionnaire administrative indépendante — Pays Basque</SectionLabel>
    </Reveal>
    <h1 className="mt-6 font-display text-4xl font-bold leading-[1.08] tracking-tight text-argia-ink sm:text-5xl lg:text-6xl" data-testid="hero-title">
      <MaskedLine delay={0.2}>Simplifiez votre</MaskedLine>
      <MaskedLine delay={0.32}>administratif,</MaskedLine>
      <MaskedLine delay={0.44}>
        concentrez-vous sur <em className="italic font-medium text-argia-red">l'essentiel.</em>
      </MaskedLine>
    </h1>
    <Reveal delay={0.7}>
      <p className="mt-8 max-w-xl text-base leading-relaxed text-argia-muted md:text-lg" data-testid="hero-subtitle">
        ARGIA Admin accompagne les artisans, commerçants, TPE, indépendants et particuliers
        dans leurs démarches administratives et l'organisation de leur activité — en présentiel ou à distance.
      </p>
    </Reveal>
    <Reveal delay={0.85}>
      <div className="mt-10 flex flex-wrap items-center gap-4">
        <Link
          to="/rendez-vous"
          data-testid="hero-cta-rdv"
          className="group inline-flex items-center gap-2 rounded-full bg-argia-red px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-argia-redDark hover:-translate-y-0.5 hover:shadow-lg"
        >
          Prendre rendez-vous
          <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
        <Link
          to="/services"
          data-testid="hero-cta-services"
          className="inline-flex items-center gap-2 rounded-full border border-argia-ink px-7 py-3.5 text-sm font-semibold text-argia-ink transition-all duration-300 hover:bg-argia-ink hover:text-white"
        >
          Découvrir mes services
        </Link>
      </div>
    </Reveal>
    <Reveal delay={1}>
      <div className="mt-12 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3 text-sm text-argia-muted">
          <ShieldCheck size={18} strokeWidth={1.5} className="text-argia-gold" />
          Assurance RC Professionnelle
        </div>
        <div className="flex items-center gap-3 rounded-lg bg-white px-4 py-2.5 shadow-sm border border-[#2C2A29]/10">
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-argia-muted">Franchisée</span>
          <img src="/assets/logo-aadprox-wide.png" alt="Franchisée du réseau AADPROX" className="h-9 w-auto" data-testid="hero-aadprox-logo" />
        </div>
      </div>
    </Reveal>
  </div>
);

const HeroImage = ({ imgY }) => (
  <div className="relative lg:col-span-5">
    <motion.div {...IMG_ANIM} className="relative overflow-hidden rounded-2xl" style={CLIP}>
      <motion.img
        src={HERO_IMG}
        alt="Aurélie Violet, gestionnaire administrative, à son bureau"
        data-testid="hero-image"
        className="max-h-[80vh] w-full object-cover"
        style={{ y: imgY, scale: 1.15 }}
      />
    </motion.div>
    <motion.div {...BADGE_ANIM} className="absolute -bottom-6 -left-6 rounded-xl bg-argia-gold px-6 py-4 shadow-lg">
      <p className="font-display text-3xl font-bold text-argia-ink">1<sup>er</sup></p>
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-argia-ink/80">Rendez-vous offert</p>
    </motion.div>
  </div>
);

export const Hero = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <section ref={heroRef} className="relative overflow-hidden grain">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 pb-20 pt-16 lg:grid-cols-12 lg:px-10 lg:pt-24">
        <HeroText />
        <HeroImage imgY={imgY} />
      </div>
    </section>
  );
};
